import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button } from '@curves/ui-base';
import { Icons } from '@curves/ui-base';
import { useHistory, useLocation } from 'react-router-dom';
import { useMainLayoutContext } from '@Local/Components/Layout/Context/MainLaydoutContext';
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from '@Constants/dataEnums';
import { MEMBER_CYCLE_PURCHASE_ROUTER } from '@Router/routerPath';
import { CyclePurchasePendingChangeList } from '@Local/CyclePurchase/Components';
import useMemberCyclePurchaseApi from '@Apis/useMemberCyclePurchaseApi';
import useCyclePurchaseEffectDetails from '@Local/CyclePurchase/hooks/useCyclePurchaseEffectDetails';
import useMessageDialog from '@Util/hook/useMessageDialog';

/**
 * 循環訂單預約變更歷史頁
 * 顯示單筆訂單所有「待生效」變更紀錄。
 * 入口：CyclePurchasePage → handleViewHistory（location.state: { order }）
 */
const CyclePurchasePendingChangePage = () => {
    const { setHeaderType, setFooterType, setThemeType } = useMainLayoutContext();
    const history = useHistory();
    const location = useLocation();
    const { order } = location.state || {};

    // 功能：預約變更紀錄清單
    const [records, setRecords] = useState([]);
    const { getMemberCyclePurchasePendingChangesApi, deleteMemberCyclePurchaseEffectionApi } = useMemberCyclePurchaseApi();
    const setMessageDialog = useMessageDialog();

    // 功能：卡片內就地展開詳情的狀態（展開集合／載入中集合／詳情快取）
    const { expandedIds, loadingIds, detailMap, toggle, clear } = useCyclePurchaseEffectDetails(order?.cycleOrderID);

    // 功能：設定版面
    useEffect(() => {
        setHeaderType(HEADER_TYPE.DEFAULT);
        setFooterType(FOOTER_TYPE.HIDE);
        setThemeType(THEME_TYPE.DARK);
    }, [setHeaderType, setFooterType, setThemeType]);

    // 功能：載入該訂單的預約變更紀錄（僅在 order 存在時由守衛 effect 呼叫）
    const fetchRecords = useCallback(async () => {
        const data = await getMemberCyclePurchasePendingChangesApi(order.cycleOrderID);
        if (data) setRecords(data.pendingChanges ?? []);
        // eslint-disable-next-line
    }, [order]);

    // 功能：守衛（未傳訂單時轉回列表）＋初次載入
    useEffect(() => {
        if (!order) {
            history.replace(MEMBER_CYCLE_PURCHASE_ROUTER);
            return;
        }
        fetchRecords();
    }, [history, order, fetchRecords]);

    // 功能：刪除預約變更單（二次確認後執行，成功後清除該筆展開狀態並重抓清單）
    const handleDelete = (record) => {
        setMessageDialog({
            open: true,
            title: '刪除預約變更',
            msg: `確定要刪除「${record.effectTypeText.join('、')}」這筆預約變更嗎？刪除後將無法復原。`,
            buttonText: '刪除',
            onAsyncConfirm: async (e, closeDialog) => {
                const result = await deleteMemberCyclePurchaseEffectionApi(record.effectID);
                if (result) {
                    clear(record.effectID);
                    await fetchRecords();
                }
                closeDialog();
            },
        });
    };

    if (!order) return null;

    return (
        <Box className="cycle-purchase-history-page">
            <Box className="cp-history-page-header">
                <Button className="cp-history-back-btn" startIcon={<Icons.KeyboardArrowLeft />} onClick={() => history.goBack()} style={{ color: 'white' }}>
                    返回 我的循環訂單
                </Button>
                <Typography className="cp-history-section-header">預約變更</Typography>
                <Typography className="cp-history-order-meta">
                    {order.cycleOrderNo}
                    {records.length > 0 ? `，共 ${records.length} 筆待生效變更` : ''}
                </Typography>
            </Box>

            <Box className="container">
                <CyclePurchasePendingChangeList
                    records={records}
                    expandedIds={expandedIds}
                    loadingIds={loadingIds}
                    detailMap={detailMap}
                    onToggleDetail={toggle}
                    onDelete={handleDelete}
                />
            </Box>
        </Box>
    );
};

export default CyclePurchasePendingChangePage;
